import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosApi from "../../api/axiosInstance";
import ScaleLoader from "react-spinners/ScaleLoader";
import { toast } from "react-toastify";

// ✅ Module-level tracking to prevent duplicate calls
const globalProcessingOrders = new Map();

export const OrderConfirmationV2 = () => {
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [orderId, setOrderId] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const componentMounted = useRef(true);

  // Extract order reference from URL
  const getOrderReference = () => {
    const params = new URLSearchParams(location.search);
    return params.get("ref");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // First useEffect - Extract orderId
  useEffect(() => {
    const orderRef = getOrderReference();

    if (!orderRef) {
      setError("Order reference not found in URL");
      setLoading(false);
      return;
    }

    if (orderRef.length !== 36) {
      setError("Invalid order reference format");
      setLoading(false);
      return;
    }

    setOrderId(orderRef);
  }, []);

  // Second useEffect - Call API with global lock
  useEffect(() => {
    const confirmOrder = async () => {
      if (!orderId || orderId === "") {
        return;
      }

      // ✅ Check if another instance is processing this order
      if (globalProcessingOrders.has(orderId)) {
        const existing = globalProcessingOrders.get(orderId);

        //console.log("🔒 Order already being processed, waiting for result...");

        try {
          const result = await existing.promise;

          if (!componentMounted.current) return;

          //console.log("✅ Received shared result from other request");

          if (result.success) {
            setOrderData(result);
            if (result.cached) {
              toast.info(`Order already confirmed ${result.processedAgo} ago`);
            } else {
              toast.success("Order confirmed successfully!");
            }
          } else {
            setError(result.message);
            toast.error(result.message);
          }

          setLoading(false);
        } catch (err) {
          if (!componentMounted.current) return;

          console.error("Error:", err);
          setError(err.message);
          setLoading(false);
        }

        return;
      }

      // ✅ This is the first instance - make the API call
      console.log("🔓 Acquired processing lock for order:", orderId.slice(-6));

      const apiPromise = (async () => {
        try {
          console.log("🚀 Confirming order:", orderId);

          const response = await axiosApi.get(
            `${process.env.REACT_APP_ZULAL_API_URL}/api/v2/customer/orders/orderconfirmation?ref=${orderId}`
          );

          console.log("✅ Order confirmation response:", response.data);

          const result = response.data;

          if (result.success) {
            if (result.cached) {
              console.log(
                `⚡ Result was cached (processed ${result.processedAgo} ago)`
              );
            }
          }

          return result;
        } catch (err) {
          console.error("❌ Error confirming order:", err);
          throw err;
        } finally {
          console.log("🔓 Released processing lock");

          setTimeout(() => {
            globalProcessingOrders.delete(orderId);
          }, 5000);
        }
      })();

      globalProcessingOrders.set(orderId, { promise: apiPromise });

      try {
        const result = await apiPromise;

        if (!componentMounted.current) return;

        if (result.success) {
          setOrderData(result);
          if (result.cached) {
            toast.info(`Order already confirmed ${result.processedAgo} ago`);
          } else {
            toast.success("Order confirmed successfully!");
          }
        } else {
          setError(result.message || "Failed to confirm order");
          toast.error(result.message || "Failed to confirm order");
        }
      } catch (err) {
        if (!componentMounted.current) return;

        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to confirm order. Please contact support.";

        setError(errorMessage);
        toast.error(errorMessage);

        if (err.response?.status === 401) {
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }
      } finally {
        if (componentMounted.current) {
          setLoading(false);
        }
      }
    };

    confirmOrder();
  }, [orderId, navigate]);

  const getShortOrderRef = (ref) => {
    return ref ? ref.slice(-6).toUpperCase() : "N/A";
  };

  if (loading) {
    return (
      <div className="mb-4">
        <div className="flex flex-col mx-auto items-center justify-center text-center justify-items-center min-h-screen">
          <ScaleLoader
            color="#36d7b7"
            height={40}
            loading={loading}
            radius={2}
            width={4}
          />
          <p className="mt-4 text-lg text-gray-600">Processing your order...</p>
        </div>
      </div>
    );
  }

  if (error || !orderData?.success) {
    return (
      <div className="mb-4">
        <div className="flex flex-col md:flex-row mx-auto items-center justify-center text-center justify-items-center min-h-screen">
          <div>
            <img
              src="../../images/OrderCancel.jpg"
              width={450}
              alt="Order Failed"
            />
          </div>
          <div className="max-w-md p-6">
            <h1 className="text-4xl md:text-6xl font-bold text-red-600 mb-4">
              Order Not Confirmed
            </h1>

            <div className="text-base text-red-600 font-normal md:text-xl mb-6">
              {error || "Unable to confirm your order"}
            </div>

            <div className="text-base text-gray-600 font-normal md:text-lg mb-6">
              If you have any questions or concerns, please contact our customer
              support team at 600-55-66-66.
            </div>

            <Link
              to="/login"
              className="flex w-full items-center justify-center rounded-md border border-transparent bg-primary mt-4 py-3 px-6
                text-white hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              <span>Go to Login</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex flex-col md:flex-row mx-auto items-center justify-center text-center justify-items-center min-h-screen">
        <div>
          <img
            src="../../images/orderConfirmation.jpg"
            width={450}
            alt="Order Confirmed"
          />
        </div>
        <div className="max-w-md p-6">
          <h1 className="text-4xl md:text-6xl font-bold text-green-600 mb-4">
            Thank You for Your Order!
          </h1>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-600 mb-2">Order Reference</div>
            <div className="text-2xl font-bold text-green-700">
              #{getShortOrderRef(orderData.orderReference)}
            </div>
          </div>

          <div className="text-left bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Order Total:</span>
              <span className="font-semibold">
                AED {orderData.orderTotal?.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Status:</span>
              <span className="font-semibold text-green-600">
                {orderData.paymentStatus}
              </span>
            </div>
            {orderData.deliveryLocation &&
              orderData.deliveryLocation !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery:</span>
                  <span className="font-semibold text-sm">
                    {orderData.deliveryLocation}
                  </span>
                </div>
              )}
            {orderData.isDonation && (
              <div className="flex justify-between">
                <span className="text-gray-600">Mosque Donation:</span>
                <span className="font-semibold text-blue-600">
                  {orderData.mosqueName}
                </span>
              </div>
            )}
            {orderData.cached && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-sm text-blue-800">
                  ⚡ <strong>Order Already Confirmed</strong>
                  <br />
                  Processed {orderData.processedAgo} ago
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-gray-600 mb-6">
            You will receive an SMS confirmation shortly. Our team will process
            your order soon.
          </div>

          <Link
            to="/user/dashboard"
            className="flex w-full items-center justify-center rounded-md border border-transparent bg-primary py-3 px-6
              text-white hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            <span>Go to Dashboard</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
